import React, { useState } from 'react';
import { createEmployee } from '../../services/simpleEmployeeService';
import { allocateComps } from '../../services/simpleCompService';
import styles from './CreateUserModal.module.css';

interface CreateUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    is_admin: false,
    // Comp allocation
    drinks: 2,
    food: 1,
    expiresIn: '7', // days
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdPin, setCreatedPin] = useState<string | null>(null);
  const [showPin, setShowPin] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setCreatedPin(null);

    try {
      // Validate inputs
      if (!formData.name) {
        setError('Please enter a name');
        setLoading(false);
        return;
      }

      // Create employee with auto-generated PIN
      const { data: employee, error: createError, pin } = await createEmployee({
        name: formData.name,
        email: formData.email || undefined,
        is_admin: formData.is_admin,
      });

      if (createError || !employee) {
        setError(createError?.message || 'Failed to create employee');
        setLoading(false);
        return;
      }

      // Calculate expiration date
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + parseInt(formData.expiresIn));

      // Allocate initial comps
      if (formData.drinks > 0 || formData.food > 0) {
        const { error: allocError } = await allocateComps(
          employee.id,
          formData.drinks,
          formData.food,
          expiresAt.toISOString(),
          'Initial allocation'
        );

        if (allocError) {
          setError('Employee created but failed to allocate comps: ' + allocError.message);
          setLoading(false);
          return;
        }
      }

      // Show the generated PIN
      setCreatedPin(pin || 'Unknown');
      setShowPin(true);
      onSuccess();

      // Don't close immediately - let user see the PIN
      setLoading(false);
    } catch (err: any) {
      setError('An unexpected error occurred: ' + err.message);
      setLoading(false);
    }
  };

  const handleDone = () => {
    onClose();
    // Reset form
    setFormData({
      name: '',
      email: '',
      is_admin: false,
      drinks: 2,
      food: 1,
      expiresIn: '7',
    });
    setCreatedPin(null);
    setShowPin(false);
  };

  // If PIN was created, show it
  if (showPin && createdPin) {
    return (
      <div className={styles.overlay} onClick={handleDone}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <h2>Employee Created!</h2>
          </div>

          <div className={styles.pinDisplay}>
            <p className={styles.pinLabel}>Share this PIN with the employee:</p>
            <div className={styles.pinValue}>{createdPin}</div>
            <p className={styles.pinHint}>They can use this PIN to log in at /employee/login</p>
          </div>

          <div className={styles.actions}>
            <button onClick={handleDone} className={styles.submitButton}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Create New Employee</h2>
          <button onClick={onClose} className={styles.closeButton}>×</button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Jane Doe"
              disabled={loading}
              required
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="email">Email (optional)</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="jane@example.com"
              disabled={loading}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="role">Role</label>
            <select
              id="role"
              value={formData.is_admin ? 'admin' : 'employee'}
              onChange={(e) => setFormData({ ...formData, is_admin: e.target.value === 'admin' })}
              disabled={loading}
              className={styles.select}
            >
              <option value="employee">Employee</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className={styles.sectionDivider}>
            <span>Initial Comp Allocation</span>
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.field}>
              <label htmlFor="drinks">Drink Comps</label>
              <input
                type="number"
                id="drinks"
                value={formData.drinks}
                onChange={(e) => setFormData({ ...formData, drinks: parseInt(e.target.value) || 0 })}
                min="0"
                disabled={loading}
                className={styles.numberInput}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="food">Food Comps</label>
              <input
                type="number"
                id="food"
                value={formData.food}
                onChange={(e) => setFormData({ ...formData, food: parseInt(e.target.value) || 0 })}
                min="0"
                disabled={loading}
                className={styles.numberInput}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="expiresIn">Expires In</label>
              <select
                id="expiresIn"
                value={formData.expiresIn}
                onChange={(e) => setFormData({ ...formData, expiresIn: e.target.value })}
                disabled={loading}
                className={styles.select}
              >
                <option value="1">1 Day</option>
                <option value="7">1 Week</option>
                <option value="30">1 Month</option>
              </select>
            </div>
          </div>

          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={styles.cancelButton}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={styles.submitButton}
            >
              {loading ? 'Creating...' : 'Create Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
